import { formatDateString } from "@/v2-common/utils/utils.date"
import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { LICENSE_PLAN_STATUS } from "@/v2-backoffice-client/license/plan/license.plan.constants"
import { getLicensePlanStatus } from "@/v2-backoffice-client/license/plan/license.plan.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

export type LicensePlanStatusBadgeProps = {
  plan: Pick<
    LicensePlanFragment,
    | "ProductCode"
    | "DeprecatedAt"
    | "DeprecatedByUserName"
    | "DeprecationNote"
    | "SuccessorPlanName"
    | "LiveLicenseCount"
  >
}

// The Licenses board's Status cell — the license sibling of
// CustomerCompanyBadge. A plain dot while the license is live; on a license
// without a product a hover panel saying why the row's usage columns are
// blank and where to fix it; on a deprecated license the story behind the
// flag (when and who, the suggested successor, the operator's note, and how
// much of the migration worklist remains).
function LicensePlanStatusBadge(props: LicensePlanStatusBadgeProps) {
  const { plan } = props
  const status = getLicensePlanStatus(plan)

  if (status === LICENSE_PLAN_STATUS.Live) {
    return <DotText variant="success-1">{status}</DotText>
  }

  if (status === LICENSE_PLAN_STATUS.NeedsSetup) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <DotText variant="destructive">{status}</DotText>
        </Tooltip.Trigger>

        <Tooltip.Content side="top" className="ui-tooltip-content--panel">
          <div className="flex max-w-xs flex-col gap-1 p-3">
            <DotText
              variant="destructive"
              className="flex font-medium text-popover-foreground"
            >
              No product yet
            </DotText>

            <p className="text-muted-foreground">
              Until an operator says which product this license is for, the
              Fenerum import skips its subscriptions: no customer is put on it,
              and Customers, Seats and MRR stay blank here — unknown, not zero.
            </p>

            <p className="mt-1 text-xs text-muted-foreground">
              Fix it with Update License in the row menu.
            </p>
          </div>
        </Tooltip.Content>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant="warning">{status}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-1 p-3">
          <DotText
            variant="warning"
            className="flex font-medium text-popover-foreground"
          >
            Deprecated {formatDateString(plan.DeprecatedAt)}
            {plan.DeprecatedByUserName
              ? ` by ${plan.DeprecatedByUserName}`
              : ""}
          </DotText>

          <p className="text-muted-foreground">
            {plan.SuccessorPlanName
              ? `Suggested successor: ${plan.SuccessorPlanName}.`
              : "No successor declared — targets are agreed per customer."}
          </p>

          {plan.DeprecationNote && (
            <p className="text-muted-foreground">{plan.DeprecationNote}</p>
          )}

          <p className="mt-1 text-xs text-muted-foreground">
            {plan.LiveLicenseCount > 0
              ? `${plan.LiveLicenseCount} customer${plan.LiveLicenseCount === 1 ? "" : "s"} still on this license — work them from the license panel's Migration tab.`
              : "No customer remains on this license — the migration is done."}
          </p>
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default LicensePlanStatusBadge
